import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import * as momentTimezone from 'moment-timezone';

import {
    CouponBondPaymentQueueRowInterface,
    CouponBondPaymentStatusEnum,
    CouponBondPostingStatusEnum,
    CouponBondQueueFilterModel,
    HttpStatusCodesEnum,
} from '@spa/api';

import { AmountMinValidator, AmountMinValidatorKey } from '@spa/validators';

import { ColorCodesEnum, ComponentTypesEnum } from '../../app/shared/enums';
import { FilterControlInterface } from '../../app/shared/interfaces';
import {
    ReportTableColumn,
    ReportTableConfigInterface,
    ReportTableData
} from '../../app/shared/components';
import { RadioGroupOptionInterface } from '../../app/shared/components/radio-group/interfaces/radio-group-option.interface';
import { SelectOptionInterface } from '../../app/shared/components/select/interfaces/select-option.interface';
import {
    AmountControl,
    BaseControl,
    DatepickerControl,
    Helper,
    RadioGroupControl,
    SelectControl
} from '../../app/shared/classes';

// ── Selectors ──

/** The route parameter on `coupon-bonds/edit/:couponBondPaymentId`. */
export const PanelCouponBondsRequestIdSelector = 'couponBondPaymentId';

// The receipt selectors are the request field names of CouponBondReceiptInputModel, so the form
// value posts as-is.
export const CouponBondReceivedAmountSelector = 'receivedAmount';
export const CouponBondReceivedDateSelector = 'receivedDate';
export const CouponBondPaymentReferenceSelector = 'paymentReference';
export const CouponBondCommentSelector = 'comment';

export const CouponBondStatusSelector = 'couponBondPaymentStatus';

/**
 * The lifecycle as radio options, in the order an operator meets them.
 *
 * Every option is disabled: a coupon payment's status is a consequence of what the distribution
 * did, never something an operator picks. The section exists to show where the payment stands,
 * which is why it carries no Save and no Delete - unlike the Transaction Status section it is
 * modelled on.
 */
export const CouponBondPaymentStatusOptions = (): RadioGroupOptionInterface[] => [
    CouponBondPaymentStatusEnum.Upcoming,
    CouponBondPaymentStatusEnum.AwaitingAmount,
    CouponBondPaymentStatusEnum.ReadyForDistribution,
    CouponBondPaymentStatusEnum.DistributionInProgress,
    CouponBondPaymentStatusEnum.Distributed,
    CouponBondPaymentStatusEnum.PartiallyDistributed,
    CouponBondPaymentStatusEnum.DistributionListGenerationFailed,
    CouponBondPaymentStatusEnum.NoDistributionRequired,
    CouponBondPaymentStatusEnum.Expired
].map((status: number) => ({
    label: Helper.textsHelper().getCouponBondPaymentStatusName(status),
    value: status,
    color: ColorCodesEnum.Primary,
    disabled: true
}));

/**
 * The badge a status wears in the queue and on the product profile's tab.
 *
 * Signed off on FP-1722. The grouping is the operator's at-a-glance triage: warning is "you have
 * something to do", info is "the system is working on it", danger is "it broke", neutral is
 * "nothing is expected of anyone".
 */
/**
 * The same reading as the badge, as title text rather than a pill. The level-2 title carries the
 * status inline after the payment code, where a pill would compete with the code for attention.
 */
export const CouponBondPaymentStatusTextClass = (status: number): string => {
    switch (Number(status)) {
        case CouponBondPaymentStatusEnum.AwaitingAmount:
        case CouponBondPaymentStatusEnum.PartiallyDistributed:
            return 'text-warning';
        case CouponBondPaymentStatusEnum.ReadyForDistribution:
        case CouponBondPaymentStatusEnum.DistributionInProgress:
            return 'text-info';
        case CouponBondPaymentStatusEnum.Distributed:
            return 'text-success';
        case CouponBondPaymentStatusEnum.DistributionListGenerationFailed:
            return 'text-danger';
        default:
            return 'text-muted';
    }
};

export const CouponBondPaymentStatusBadgeClass = (status: number): string => {
    switch (Number(status)) {
        case CouponBondPaymentStatusEnum.AwaitingAmount:
        case CouponBondPaymentStatusEnum.PartiallyDistributed:
            return 'badge badge-pill badge-warning';
        case CouponBondPaymentStatusEnum.ReadyForDistribution:
        case CouponBondPaymentStatusEnum.DistributionInProgress:
            return 'badge badge-pill badge-info';
        case CouponBondPaymentStatusEnum.Distributed:
            return 'badge badge-pill badge-success';
        case CouponBondPaymentStatusEnum.DistributionListGenerationFailed:
            return 'badge badge-pill badge-danger';
        default:
            return 'badge badge-pill badge-secondary';
    }
};

/**
 * What the queue shows before anyone touches a filter.
 *
 * Not every status: most rows are historical, and a work queue that opens on everything is a
 * report. These five are the ones where the payment is either waiting on an operator or moving
 * through the system, which is what an operator opens the queue to see.
 */
export const CouponBondActionableStatuses: number[] = [
    CouponBondPaymentStatusEnum.AwaitingAmount,
    CouponBondPaymentStatusEnum.ReadyForDistribution,
    CouponBondPaymentStatusEnum.DistributionInProgress,
    CouponBondPaymentStatusEnum.PartiallyDistributed,
    CouponBondPaymentStatusEnum.DistributionListGenerationFailed
];

export const CouponBondStatusControl = (): RadioGroupControl => new RadioGroupControl({
    name: CouponBondStatusSelector,
    value: null,
    placeholder: 'Status',
    validators: [],
    componentType: ComponentTypesEnum.RadioGroup,
    options: CouponBondPaymentStatusOptions()
});

// ── Receipt ──

export const CouponBondAmountDecimalsValidatorKey = 'CouponBondAmountDecimalsValidatorKey';

/**
 * The received amount must be a whole number of minor units.
 *
 * `app-amount-input` already blocks a third decimal at the keystroke and clears a pasted value
 * that does not match its amount regex; this covers a value that reaches the control by any
 * other route. The same rule is enforced in alaric-fund-orders, which rejects an amount it
 * cannot split into whole cents rather than distributing a residual it invented.
 */
export const CouponBondAmountDecimalsValidator = (): ValidatorFn =>
    (control: AbstractControl): ValidationErrors | null => {
        if (control.value === null || control.value === undefined || control.value === '') {
            return null;
        }

        const decimals = String(control.value).replace(/[\s,]/g, '').split('.')[1];

        return !decimals || decimals.length <= 2 ? null : { [CouponBondAmountDecimalsValidatorKey]: true };
    };

/**
 * BR: the receipt amount is authoritative and is what gets distributed, so it must be present
 * and payable. `AmountMinValidator(0.01)` rejects both zero and a negative value - the widget
 * does not accept a minus sign unless `allowNegative` is set, and it is not set here.
 */
export const CouponBondReceivedAmountValidators = (): ValidatorFn[] => [
    Validators.required,
    AmountMinValidator(0.01),
    CouponBondAmountDecimalsValidator()
];

/**
 * Both refusals say the same sentence, which is the prototype's. Unmapped, AmountMinValidatorKey
 * falls through to the form's generic text and renders "Received amount must be minimum undefined"
 * - the widget's own message, assembled from a bound it was never told.
 */
export const CouponBondReceiptFormMessages = (): { [key: string]: string } => ({
    [CouponBondAmountDecimalsValidatorKey]: 'Enter a positive amount with up to 2 decimals.',
    [AmountMinValidatorKey]: 'Enter a positive amount with up to 2 decimals.'
});

export interface CouponBondReceiptFormControlsInterface {
    receivedAmount: AmountControl;
    receivedDate: DatepickerControl;
    paymentReference: BaseControl;
    comment: BaseControl;
}

export const CouponBondReceiptControls = (currency: string): CouponBondReceiptFormControlsInterface => ({
    receivedAmount: new AmountControl({
        name: CouponBondReceivedAmountSelector,
        value: null,
        placeholder: 'Received amount',
        validators: CouponBondReceivedAmountValidators(),
        componentType: ComponentTypesEnum.AmountInput,
        currency,
        isInitialFormattingActive: true
    }),
    receivedDate: new DatepickerControl({
        name: CouponBondReceivedDateSelector,
        value: null,
        placeholder: 'Received date',
        validators: [Validators.required],
        componentType: ComponentTypesEnum.Datepicker
    }),
    paymentReference: new BaseControl({
        name: CouponBondPaymentReferenceSelector,
        value: null,
        placeholder: 'Optional',
        validators: [Validators.maxLength(100)],
        componentType: ComponentTypesEnum.Text
    }),
    comment: new BaseControl({
        name: CouponBondCommentSelector,
        value: null,
        placeholder: 'Optional',
        validators: [Validators.maxLength(500)],
        componentType: ComponentTypesEnum.Textarea
    })
});

// ── Postings ──

/**
 * How the Payments tab narrows its rows.
 *
 * `Unresolved` is the one an operator lands on while a distribution is being chased: it is every
 * row that has not reached CRM successfully, including the ones no retry can fix.
 */
export enum CouponBondPostingFilterEnum {
    Unresolved = 1,
    Completed = 2,
    All = 3
}

export const CouponBondPostingFilterName = (filter: number): string => {
    switch (filter) {
        case CouponBondPostingFilterEnum.Unresolved:
            // Names the two statuses it selects rather than the category, as the prototype does:
            // "Unresolved" is our word for them and means nothing to the operator chasing rows.
            return 'Failed + Unknown';
        case CouponBondPostingFilterEnum.Completed:
            return 'Completed';
        case CouponBondPostingFilterEnum.All:
            return 'All';
        default:
            return '';
    }
};

/**
 * The posting status as a badge, the way the queue already shows payment status and the prototype
 * shows every adjustment row. Completed and No Posting Required are both settled outcomes, so both
 * read as success: a row that rounds to zero is finished, not outstanding.
 */
export const CouponBondPostingStatusBadgeClass = (status: number): string => {
    switch (Number(status)) {
        case CouponBondPostingStatusEnum.Completed:
        case CouponBondPostingStatusEnum.NoPostingRequired:
            return 'badge badge-pill badge-success';
        case CouponBondPostingStatusEnum.Failed:
        case CouponBondPostingStatusEnum.PostingFailed:
            return 'badge badge-pill badge-danger';
        case CouponBondPostingStatusEnum.Unknown:
        case CouponBondPostingStatusEnum.ReconciliationRequired:
            return 'badge badge-pill badge-warning';
        case CouponBondPostingStatusEnum.Creating:
        case CouponBondPostingStatusEnum.PendingPosting:
            return 'badge badge-pill badge-info';
        default:
            return 'badge badge-pill badge-secondary';
    }
};

/** The prototype's order: the rows that need chasing first, then everything, then the settled ones. */
export const CouponBondPostingFilterOptions = (): SelectOptionInterface[] => [
    CouponBondPostingFilterEnum.Unresolved,
    CouponBondPostingFilterEnum.All,
    CouponBondPostingFilterEnum.Completed
].map((filter: number) => ({ label: CouponBondPostingFilterName(filter), value: filter }));

/**
 * What Retry Failed re-arms, mirroring `RetryableStatuses` in alaric-fund-orders.
 *
 * Unknown and Reconciliation Required are deliberately absent: those rows neither confirmed nor
 * refused, and re-posting one is how a client gets paid twice. Reconciliation resolves them by
 * asking CRM instead.
 */
export const CouponBondRetryablePostingStatuses: number[] = [
    CouponBondPostingStatusEnum.Failed,
    CouponBondPostingStatusEnum.PostingFailed
];

export const CouponBondUnresolvedPostingStatuses: number[] = [
    CouponBondPostingStatusEnum.Pending,
    CouponBondPostingStatusEnum.Creating,
    CouponBondPostingStatusEnum.PendingPosting,
    CouponBondPostingStatusEnum.Failed,
    CouponBondPostingStatusEnum.PostingFailed,
    CouponBondPostingStatusEnum.Unknown,
    CouponBondPostingStatusEnum.ReconciliationRequired
];

// ── Coupon Distributions queue ──

/** Keys the cached filter, pagination and sort state; the resolver reads back the same id. */
export const PanelCouponBondsStorageId = 'PanelCouponBondsQueueComponent';

/**
 * Not a status. The endpoint takes a list but the control is a single select, so the actionable
 * set needs a value of its own to sit beside the nine; without one it has to be the empty default,
 * which is what "All" means in the prototype.
 */
export const CouponBondActionableFilterValue = -1;

/**
 * What the prototype puts in a cell that has no value yet - a historical payment's amounts, an
 * account count before generation. Not 0.00, which reads as a figure someone calculated.
 */
export const CouponBondEmptyValue = '—';

/**
 * The prototype dates these screens day-first with a four-digit year. `environment.dateFormat` is
 * `MM/DD/YY` and belongs to every other screen in the CRM, so the coupon screens carry their own
 * rather than re-date Payments and Orders along with them.
 */
export const CouponBondDateFormat = 'DD/MM/YYYY';

/** The prototype stamps adjustments as "22/09/2025, 09:49". These values are UTC on the wire. */
export const CouponBondDateTimeFormat = 'DD/MM/YYYY, HH:mm';

/**
 * A bare European decimal, for the allocation intermediates the prototype prints without a currency
 * sign: the share to four places, raw allocated to six, everything else to two. Fixed decimals on
 * purpose - trailing zeros are what make a column of figures line up and compare.
 */
/**
 * The zone the observation cut-off is defined in. fund-orders owns it per payment
 * (CouponBondConstants.ObservationCutOffTimeZoneId) but does not return it, so it is repeated here
 * to state the cut-off the way the prototype does; if it ever varies by product it must come from
 * the payload instead.
 */
export const CouponBondCutOffZone = 'Europe/Sofia';

/**
 * The cut-off as a local instant with its zone named, which is the form the rule is written in -
 * "18:30 Europe/Sofia". The payload carries the UTC instant that resolves to, and an operator
 * should not have to convert it back to check a decision against the rule.
 */
export const CouponBondCutOffText = (observationCutOffUtc: string): string => {
    if (!observationCutOffUtc) {
        return CouponBondEmptyValue;
    }

    return `${momentTimezone.utc(observationCutOffUtc).tz(CouponBondCutOffZone)
        .format(CouponBondDateTimeFormat)} ${CouponBondCutOffZone}`;
};

export const CouponBondDecimal = (value: number, decimals: number = 2): string => {
    if (value === null || value === undefined) {
        return CouponBondEmptyValue;
    }

    return value.toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

/**
 * The European amount format the owner chose and the prototype renders throughout: dot thousands,
 * comma decimals, currency sign after the number. `Helper.formatCurrency` is en-US and puts the
 * sign in front, and it is shared with every other screen in the CRM, so the coupon screens carry
 * their own rather than change what Payments and Orders already show.
 */
export const CouponBondAmount = (amount: number, currencyCode: string): string => {
    if (amount === null || amount === undefined) {
        return CouponBondEmptyValue;
    }

    const formatted = amount.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    const sign = Helper.getCurrencySign(currencyCode);

    return sign ? `${formatted} ${sign}` : formatted;
};

/** The prototype's order, which is the order of the work rather than of the enum. */
const CouponBondStatusFilterOptions = () => [
    CouponBondPaymentStatusEnum.AwaitingAmount,
    CouponBondPaymentStatusEnum.ReadyForDistribution,
    CouponBondPaymentStatusEnum.DistributionInProgress,
    CouponBondPaymentStatusEnum.PartiallyDistributed,
    CouponBondPaymentStatusEnum.Distributed,
    CouponBondPaymentStatusEnum.DistributionListGenerationFailed,
    CouponBondPaymentStatusEnum.NoDistributionRequired,
    CouponBondPaymentStatusEnum.Upcoming,
    CouponBondPaymentStatusEnum.Expired
].map((status: number) => ({
    label: Helper.textsHelper().getCouponBondPaymentStatusName(status),
    value: status
}));

/**
 * Status, Product, ISIN and a payment-date range - the four an operator narrows by (spec §38.10).
 *
 * Status is a single select rather than a multi-select even though the endpoint takes a list,
 * because the only multi-status view anyone asked for is the actionable default, and that is
 * applied when the filter is left alone rather than being something to reassemble by hand.
 */
export const PanelCouponBondsFilterControls = (): FilterControlInterface[] => [
    {
        name: CouponBondQueueFilterModel.getStatusesSelector(),
        placeholder: 'Status',
        controls: [
            new SelectControl({
                name: CouponBondQueueFilterModel.getStatusesSelector(),
                value: null,
                placeholder: 'Status',
                validators: [],
                componentType: ComponentTypesEnum.Select,
                options: [
                    { label: 'All', value: null },
                    { label: 'Actionable (needs work)', value: CouponBondActionableFilterValue },
                    ...CouponBondStatusFilterOptions()
                ]
            })
        ],
        shown: true,
        sortIndex: 1
    },
    {
        name: CouponBondQueueFilterModel.getProductCodeSelector(),
        placeholder: 'Product',
        controls: [
            new BaseControl({
                name: CouponBondQueueFilterModel.getProductCodeSelector(),
                value: null,
                placeholder: 'Product',
                validators: [],
                componentType: ComponentTypesEnum.Text
            })
        ],
        shown: true,
        sortIndex: 2
    },
    {
        name: CouponBondQueueFilterModel.getIsinSelector(),
        placeholder: 'ISIN',
        controls: [
            new BaseControl({
                name: CouponBondQueueFilterModel.getIsinSelector(),
                value: null,
                placeholder: 'ISIN',
                validators: [],
                componentType: ComponentTypesEnum.Text
            })
        ],
        shown: true,
        sortIndex: 3
    },
    {
        name: CouponBondQueueFilterModel.getPaymentDateFromSelector(),
        placeholder: 'Payment date from',
        controls: [
            new DatepickerControl({
                name: CouponBondQueueFilterModel.getPaymentDateFromSelector(),
                value: null,
                placeholder: 'Payment date from',
                validators: [],
                componentType: ComponentTypesEnum.Datepicker
            })
        ],
        shown: true,
        sortIndex: 4
    },
    {
        name: CouponBondQueueFilterModel.getPaymentDateToSelector(),
        placeholder: 'Payment date to',
        controls: [
            new DatepickerControl({
                name: CouponBondQueueFilterModel.getPaymentDateToSelector(),
                value: null,
                placeholder: 'Payment date to',
                validators: [],
                componentType: ComponentTypesEnum.Datepicker
            })
        ],
        shown: true,
        sortIndex: 5
    }
];

const QueueColumn = (key: keyof CouponBondPaymentQueueRowInterface): string => key;

/**
 * Columns confirmed on FP-1722. None are sortable: the endpoint has one order - payment date
 * ascending, nearest first - so a sort arrow would offer something the server does not do.
 */
export const PanelCouponBondsDataTableConfig = (): ReportTableConfigInterface => ({
    rows: [],
    columns: [
        new ReportTableColumn({ label: 'Payment ID', selector: QueueColumn('displayCode') }),
        new ReportTableColumn({ label: 'Product', selector: QueueColumn('productName') }),
        new ReportTableColumn({ label: 'ISIN', selector: QueueColumn('isin') }),
        new ReportTableColumn({ label: 'Payment Date', selector: QueueColumn('paymentDate') }),
        new ReportTableColumn({ label: 'Observation Date', selector: QueueColumn('observationDate') }),
        new ReportTableColumn({ label: 'Status', selector: QueueColumn('status') }),
        new ReportTableColumn({ label: 'Accounts', selector: QueueColumn('totalAccounts'), shifted: true }),
        new ReportTableColumn({ label: 'Received Amount', selector: QueueColumn('receivedAmount'), shifted: true }),
        new ReportTableColumn({ label: 'Distributed', selector: QueueColumn('successfullyDistributedAmount'), shifted: true }),
        new ReportTableColumn({ label: 'Remaining', selector: QueueColumn('remainingAmount'), shifted: true })
    ],
    // The prototype's table opens straight onto its ten columns: no settings cog, no "Display
    // columns" dropdown and no trailing control cell, all of which report-table renders unless
    // this says otherwise.
    disableColumnsOptions: true,
    hiddenColumnIds: [],
    actionMenu: false
});

export const PanelCouponBondsDataTableItems = (): ReportTableData[] =>
    PanelCouponBondsDataTableConfig().columns.map(
        (column: ReportTableColumn) => new ReportTableData<string>({
            colId: column.selector,
            value: null,
            hidden: false,
            // The prototype styles the Payment ID as the link into the payment. The whole row
            // navigates, here and in the rest of the CRM, so this names the target rather than
            // leaving the operator to discover that any cell will do.
            class: column.selector === QueueColumn('displayCode') ? 'text-primary' : '',
            // Carried from the column so the header and its cells align together; set on only one
            // of the two, a right-aligned header sits over a left-aligned column of figures.
            shifted: column.shifted
        }));


// -- Failure messages --

/**
 * Why a coupon action failed, joined to the action's own text so the operator learns both.
 *
 * 401 and 403 are deliberately absent: AuthInterceptor intercepts them and routes to the error
 * page, so neither ever reaches one of these alerts.
 */
export const CouponBondFailureCause = {
    /** Status 0 - the request never completed, so there is no server answer to report. */
    Transport: 'Error while communication with the server.',
    NotFound: 'This coupon payment no longer exists.',
    NotAcceptable: 'The payment no longer meets the conditions for this action.',
    ServerError: 'The server failed while handling the request.'
};

/**
 * Anything with no cause of its own still names its status. Saying only that something went wrong
 * is what made FP-1752 look like a network fault for an afternoon, so the number goes in front of
 * the operator and into whatever they report.
 */
export const CouponBondUnexpectedCause = (status: number): string => `The server answered HTTP ${status}.`;

const couponBondFailureCause = (status: number): string => {
    switch (status) {
        case HttpStatusCodesEnum.Unknown:
            return CouponBondFailureCause.Transport;
        case HttpStatusCodesEnum.NotFound:
            return CouponBondFailureCause.NotFound;
        case HttpStatusCodesEnum.NotAcceptable:
            return CouponBondFailureCause.NotAcceptable;
        case HttpStatusCodesEnum.InternalServerError:
            return CouponBondFailureCause.ServerError;
        default:
            return CouponBondUnexpectedCause(status);
    }
};

/**
 * The alert text for a failed coupon action.
 *
 * `rejected` (409) and `locked` (423) are whole messages because they explain themselves, and they
 * must not be merged: 409 is this payment cannot be acted on at all, 423 is that something else
 * holds the lock so nothing ran. An action that cannot answer one of them omits it, and the status
 * then falls through to a cause joined to `failed`.
 */
export const CouponBondFailureMessage = (
    status: number,
    messages: { failed: string, rejected?: string, locked?: string }
): string => {
    if ((status === HttpStatusCodesEnum.Locked) && messages.locked) {
        return messages.locked;
    }

    if ((status === HttpStatusCodesEnum.Conflict) && messages.rejected) {
        return messages.rejected;
    }

    return `${couponBondFailureCause(status)} ${messages.failed}`;
};
